import React, { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CheckCircle, AlertCircle, Info, AlertTriangle, PlusCircle } from 'lucide-react';
import { CreateFieldInline } from '@/components/ui/create-field-inline';
import type { DatabaseField } from '@/lib/column-mapping-utils';

interface ColumnMapping {
  csvColumn: string;
  dbField: string | null;
  isRequired: boolean;
  dataType: string;
  sample: string;
  confidence: 'exact' | 'fuzzy' | 'none';
}

interface ColumnMapperProps {
  csvHeaders: string[];
  csvData: any[][];
  dbFields: {
    name: string;
    label: string;
    required: boolean;
    type: string;
    description?: string;
    isCustom?: boolean;
  }[];
  mappings: ColumnMapping[];
  onMappingChange: (csvColumn: string, dbField: string | null) => void;
  onFieldCreated?: (field: DatabaseField, forCsvColumn: string) => void;
  targetTable?: 'companies' | 'contacts';
}

export function ColumnMapper({
  csvHeaders,
  csvData,
  dbFields,
  mappings,
  onMappingChange,
  onFieldCreated,
  targetTable = 'companies'
}: ColumnMapperProps) {
  const [creatingFieldForColumn, setCreatingFieldForColumn] = useState<string | null>(null);

  const getSampleData = (columnIndex: number) => {
    const samples = csvData
      .slice(0, 3)
      .map(row => row[columnIndex])
      .filter(val => val && val.trim())
      .slice(0, 2);
    return samples.join(', ') || 'No data';
  };

  const getUsedFields = () => {
    return mappings
      .filter(m => m.dbField)
      .map(m => m.dbField);
  };

  const getMappingStatus = (mapping: ColumnMapping) => {
    if (!mapping.dbField && mapping.isRequired) {
      return { status: 'error', message: 'Required field not mapped' };
    }
    if (mapping.dbField) {
      return { status: 'success', message: 'Mapped correctly' };
    }
    return { status: 'info', message: 'Optional field' };
  };

  const getConfidenceBadge = (mapping: ColumnMapping) => {
    if (!mapping.dbField) return null;

    switch (mapping.confidence) {
      case 'exact':
        return (
          <Badge variant="default" className="bg-green-600 text-white text-xs gap-1">
            <CheckCircle className="w-3 h-3" />
            Exact
          </Badge>
        );
      case 'fuzzy':
        return (
          <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 border-yellow-300 text-xs gap-1">
            <AlertTriangle className="w-3 h-3" />
            Fuzzy
          </Badge>
        );
      default:
        return null;
    }
  };

  const getRequiredFieldsStatus = () => {
    const requiredFields = dbFields.filter(f => f.required);
    const mappedRequired = mappings.filter(m => m.dbField && dbFields.find(f => f.name === m.dbField)?.required);
    return {
      total: requiredFields.length,
      mapped: mappedRequired.length
    };
  };

  const usedFields = getUsedFields();
  const requiredStatus = getRequiredFieldsStatus();

  // Separate built-in and custom fields for grouped display
  const builtInFields = dbFields.filter(f => !f.isCustom);
  const customFields = dbFields.filter(f => f.isCustom);

  return (
    <div className="space-y-6">
      {/* Header with status */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Map CSV Columns to CRM Fields</h3>
          <p className="text-sm text-muted-foreground">
            Match your CSV columns with the correct CRM fields. Auto-suggestions have been applied.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={requiredStatus.mapped === requiredStatus.total ? "default" : "destructive"}>
            Required: {requiredStatus.mapped}/{requiredStatus.total}
          </Badge>
        </div>
      </div>

      {/* Mapping Grid */}
      <div className="grid gap-3 overflow-hidden">
        {mappings.map((mapping, index) => {
          const status = getMappingStatus(mapping);
          const StatusIcon = status.status === 'success' ? CheckCircle :
                           status.status === 'error' ? AlertCircle : Info;

          return (
            <Card key={mapping.csvColumn} className="relative overflow-hidden">
              <CardContent className="py-4 px-5">
                <div className="flex items-center gap-4">
                  {/* CSV Column Name + Sample */}
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="flex items-center gap-2">
                      <StatusIcon className={`w-4 h-4 flex-shrink-0 ${
                        status.status === 'success' ? 'text-green-500' :
                        status.status === 'error' ? 'text-red-500' : 'text-gray-400'
                      }`} />
                      <span className="font-medium text-sm truncate">
                        {mapping.csvColumn}
                      </span>
                      {getConfidenceBadge(mapping)}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 ml-6 truncate">
                      Sample: {getSampleData(index)}
                    </p>
                  </div>

                  {/* Arrow */}
                  <span className="text-muted-foreground text-lg flex-shrink-0">→</span>

                  {/* DB Field Selector */}
                  <div className="w-[260px] flex-shrink-0">
                    <Select
                      value={mapping.dbField || "skip-column"}
                      onValueChange={(value) => {
                        if (value === "__create_new__") {
                          setCreatingFieldForColumn(mapping.csvColumn);
                          return;
                        }
                        setCreatingFieldForColumn(null);
                        onMappingChange(mapping.csvColumn, value === "skip-column" ? null : value);
                      }}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select a field..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="skip-column">
                          <span className="text-muted-foreground">Don't import</span>
                        </SelectItem>
                        {builtInFields.map((field) => (
                          <SelectItem
                            key={field.name}
                            value={field.name}
                            disabled={usedFields.includes(field.name) && mapping.dbField !== field.name}
                          >
                            <div className="flex items-center gap-2">
                              <span>{field.label}</span>
                              {field.required && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0">Req</Badge>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                        {customFields.length > 0 && (
                          <>
                            <Separator className="my-1" />
                            <div className="px-2 py-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                              Custom Fields
                            </div>
                            {customFields.map((field) => (
                              <SelectItem
                                key={field.name}
                                value={field.name}
                                disabled={usedFields.includes(field.name) && mapping.dbField !== field.name}
                              >
                                <div className="flex items-center gap-2">
                                  <span>{field.label}</span>
                                  <Badge variant="outline" className="text-[10px] px-1 py-0 bg-blue-50 text-blue-700 border-blue-200">
                                    Custom
                                  </Badge>
                                </div>
                              </SelectItem>
                            ))}
                          </>
                        )}
                        <Separator className="my-1" />
                        <SelectItem value="__create_new__">
                          <div className="flex items-center gap-2 text-blue-600">
                            <PlusCircle className="w-3.5 h-3.5" />
                            <span className="font-medium">Create new field</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {mapping.dbField && (
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {dbFields.find(f => f.name === mapping.dbField)?.description}
                      </p>
                    )}
                  </div>
                </div>

                {/* Inline field creation form */}
                {creatingFieldForColumn === mapping.csvColumn && (
                  <div className="mt-3 ml-6">
                    <CreateFieldInline
                      targetTable={targetTable}
                      defaultLabel={mapping.csvColumn}
                      onCreated={(field) => {
                        setCreatingFieldForColumn(null);
                        if (onFieldCreated) {
                          onFieldCreated(field, mapping.csvColumn);
                        }
                      }}
                      onCancel={() => setCreatingFieldForColumn(null)}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Summary */}
      <Card className="bg-muted/50">
        <CardContent className="pt-6">
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-foreground">{mappings.length}</p>
              <p className="text-sm text-muted-foreground">CSV Columns</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">
                {mappings.filter(m => m.dbField && m.confidence === 'exact').length}
              </p>
              <p className="text-sm text-muted-foreground">Exact Matches</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-600">
                {mappings.filter(m => m.dbField && m.confidence === 'fuzzy').length}
              </p>
              <p className="text-sm text-muted-foreground">Fuzzy Matches</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-400">
                {mappings.filter(m => !m.dbField).length}
              </p>
              <p className="text-sm text-muted-foreground">Ignored</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
